import { FC } from 'react';
import { CodeBlock } from '@/components/ui/CodeBlock';
import { Card } from '@/components/ui/Card';
import stepOneImage from '@/assets/11.png';
import stepTwoImage from '@/assets/22.png';
import stepThreeImage from '@/assets/33.png';

interface WindowsInstallationProps {
  systemId: string;
  interfaceUrl?: string;
}

const environmentSetupSteps = [
  {
    description: '打开“文件夹”或者“我的电脑”，右键左侧“电脑图标”后左键点击“属性”，如下图。',
    image: stepOneImage,
  },
  {
    description: '左键点击“高级系统设置”，再左键点击“环境变量”，如下图。',
    image: stepTwoImage,
  },
  {
    description: '新建 4 个环境变量后，点击“确认”即可，如下图。',
    image: stepThreeImage,
  },
];

export const WindowsInstallation: FC<WindowsInstallationProps> = ({ interfaceUrl }) => {
  // 使用API返回的完整URL，如果没有则使用fallback
  const baseUrl = interfaceUrl || `你的API_URL如：http://xx.xx.xx.xx:8080`;
  const manualEnvConfig = `export ANTHROPIC_BASE_URL=${baseUrl}
export ANTHROPIC_AUTH_TOKEN="你得key"
export CLAUDE_CODE_DISABLE_NONESSENTIAL_TRAFFIC=1
export CLAUDE_CODE_ATTRIBUTION_HEADER=0`;
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Windows 安装步骤</h2>
        <p className="text-gray-600 leading-relaxed">
          在 Windows 系统上安装白茆AI (Claude Code)，请按照以下步骤操作。
        </p>
      </div>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">1️⃣</span>
          【此步骤在桌面】下载git
        </h3>
        <p className="text-gray-600 mb-4">
          访问 <a href="https://git-scm.com/downloads/win" className="text-primary-600 hover:underline">https://git-scm.com/downloads/win</a>，安装时全都下一步，不要修改路径
        </p>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">2️⃣</span>
          【此步骤在桌面】下载nodejs
        </h3>
        <p className="text-gray-600 mb-4">
          访问 <a href="https://nodejs.org/zh-cn/download" className="text-primary-600 hover:underline">https://nodejs.org/zh-cn/download</a>，安装时全都下一步，不要修改路径
        </p>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">3️⃣</span>
          【此步骤在Windows PowerShell】验证安装情况
        </h3>
        <p className="text-gray-600 mb-2">
          打开Windows PowerShell（蓝色图标），执行以下命令验证安装情况：
        </p>
        <CodeBlock
          code={`PS> node -v
PS> npm -v`}
          title="验证安装"
        />
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm">
            若提示「No suitable shell found」，是git没装好，请将 <code className="bg-yellow-100 px-1 py-0.5 rounded">CLAUDE_CODE_GIT_BASH_PATH=C:\Program Files\git\bin\bash.exe</code> 设置到系统环境变量中，重启终端后再试。依旧无效，重装git，重启终端后再试
          </p>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">4️⃣</span>
          【此步骤在Windows PowerShell】卸载已安装的Claude Code（未安装请跳过）
        </h3>
        <CodeBlock
          code="PS> npm uninstall -g @anthropic-ai/claude-code"
          title="卸载旧版本"
        />
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">⭐</span>
          【此步骤在Windows PowerShell】安装官方原版包
        </h3>
        <CodeBlock
          code="PS> npm install -g @anthropic-ai/claude-code"
          title="安装Claude Code"
        />
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">6️⃣</span>
          【此步骤在控制面板】设置Windows系统环境变量
        </h3>
        <p className="text-gray-600 mb-4">需要设置以下 4 个环境变量：</p>

        <CodeBlock
          code={manualEnvConfig}
          title="环境变量配置"
        />

        <details className="group mt-5 border border-gray-200 rounded-lg bg-gray-50">
          <summary className="cursor-pointer select-none px-4 py-3 font-medium text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
            详细环境变量配置步骤
          </summary>
          <ol className="border-t border-gray-200 p-4 space-y-6">
            {environmentSetupSteps.map((step, index) => (
              <li key={step.description} className="space-y-3">
                <p className="text-sm leading-6 text-gray-700">
                  <span className="font-semibold text-gray-900">第{index + 1}步：</span>
                  {step.description}
                </p>
                <img
                  src={step.image}
                  alt={`Windows 环境变量配置第${index + 1}步示意图`}
                  className="block w-full max-h-[32rem] rounded-lg border border-gray-200 bg-white object-contain"
                />
              </li>
            ))}
          </ol>
        </details>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">✅</span>
          【此步骤在Windows PowerShell】重启Windows PowerShell，验证安装结果
        </h3>
        <p className="text-gray-600 mb-2">
          打开新的终端运行以下命令，确认安装成功
        </p>
        <CodeBlock
          code="PS> claude            "
          title="输入claude 后一直回车，然后输入 ： hello 查看是否可以正确返回内容"
        />
      </Card>

      <div className="bg-gradient-to-r from-primary-600 to-accent-500 rounded-lg p-6 text-white">
        <h3 className="text-lg font-semibold mb-2">🎉 安装完成！</h3>
        <p className="text-primary-100 mb-4">
          恭喜您已完成白茆AI在Windows系统的安装！现在可以开始使用AI编程助手了。
        </p>
        <div className="flex gap-3">
          <a
            href="#configuration"
            className="btn-secondary border-white text-white hover:bg-white hover:text-primary-600 inline-block px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            配置 API Key →
          </a>
        </div>
      </div>
    </div>
  );
};
